import { memo, useMemo } from 'react'
import { Virtuoso } from 'react-virtuoso'
import { ChatMessage } from './ChatMessage'
import type { Message } from '../../../types/chat.types'
import type { Agent } from '../../../services/agentService'

interface VirtualMessageListProps {
    messages: Message[]
    streamingMessage: any
    agentName: string
    agentAvatar?: string | null
    agent?: Agent | null
    onCopy?: (content: string, index: number) => void
    onFeedback?: (index: number, feedback: 'liked' | 'disliked') => void
    copiedMessageId?: number | null
}

export const VirtualMessageList = memo(({
    messages,
    streamingMessage,
    agentName,
    agentAvatar,
    agent,
    onCopy,
    onFeedback,
    copiedMessageId
}: VirtualMessageListProps) => {

    // Combine committed messages + streaming message into one virtual list
    const data = useMemo(() => {
        if (streamingMessage && streamingMessage.isStreaming) {
            return [...messages, {
                id: 'streaming-placeholder', // Stable ID for virutalizer
                conversation_id: 'streaming',
                role: 'assistant',
                content: streamingMessage.content,
                created_at: new Date().toISOString(),
                isHtml: false,
                isStreaming: true // Marker
            } as Message]
        }
        return messages
    }, [messages, streamingMessage])

    return (
        <Virtuoso
            style={{ height: '100%', width: '100%' }}
            data={data}
            followOutput={'auto'}
            initialTopMostItemIndex={data.length - 1} // Start at bottom
            alignToBottom={true} // Sticky behavior
            itemContent={(index, message) => (
                <div className="py-2 px-4">
                    <ChatMessage
                        key={message.id || index}
                        message={message}
                        index={index}
                        agentName={agentName}
                        agentAvatar={agentAvatar}
                        agent={agent}
                        onCopy={onCopy}
                        onFeedback={onFeedback}
                        copiedMessageId={copiedMessageId}
                    />
                </div>
            )}
            // Optimization: Increase overscan for smoother scrolling
            overscan={200}
        />
    )
})
